import React from "react";
import type { ICellRendererParams } from "ag-grid-community";
import type {
  UserUserDeactivateActivateInput,
  UserUserIO,
} from "../../../../generated/fresh-guard-api";
import type { ActionsCellType } from "./ActionsCell.type.ts";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { toastOptions } from "../../../../tools/helpers/ToastHelper.ts";
import { useUserManagement } from "../../../../provider/user-management/UserManagement.hook.ts";
import { useUsersDeactivateActivateOptimistic } from "../../../../apis/user/useUsersDeactivateActivateOptimistic.ts";
import { lsLoad } from "../../../../tools/Storage.ts";
import StorageEnum from "../../../../enums/Storage.enum.ts";

export const useActionsCellController = (
  props: ICellRendererParams<UserUserIO> & ActionsCellType,
) => {
  const username = lsLoad(StorageEnum.UserProfile).username;
  const { t } = useTranslation();
  const { updateDeactivatedOrActivatedUsers } = useUserManagement();
  const row = props.data;

  const { mutate, isPending } = useUsersDeactivateActivateOptimistic();

  const onEdit = (e: React.MouseEvent) => {
    e.stopPropagation();

    props.setCurrentUser(row);
    props.setIsUserDialogOpen(true);
  };

  const onDeactivate = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (row?.username) {
      callDeactivateActivateApi({
        usersForDeactivation: [row.username],
      });
    }
  };

  const onActivate = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (row?.username) {
      callDeactivateActivateApi({
        usersForActivation: [row.username],
      });
    }
  };

  const callDeactivateActivateApi = (
    userUserDeactivateActivateInput: UserUserDeactivateActivateInput,
  ) => {
    mutate(
      { userUserDeactivateActivateInput, currentUsername: username },
      {
        onSuccess: () => {
          updateDeactivatedOrActivatedUsers(userUserDeactivateActivateInput);
          toast.success(t("success.userSaved"), toastOptions);
        },
      },
    );
  };

  const onSelectMarkets = (e: React.MouseEvent) => {
    e.stopPropagation();

    props.setCurrentUser(row);
    props.setIsUserMarketsDialogOpen(true);
  };

  const onResetPassword = (e: React.MouseEvent) => {
    e.stopPropagation();

    props.setCurrentUser(row);
    props.setIsUserResetPasswordDialogOpen(true);
  };

  return {
    onEdit,
    onDeactivate,
    onActivate,
    onSelectMarkets,
    onResetPassword,
    loading: isPending,
  };
};
